// Enhanced Customers.js with premium UI design
// pages/Customers.js - Premium redesigned customer cards

import { useState, useEffect, useRef } from 'react';
import { ContentLayout, PageContainer } from '../components/layout';
import { Card, CardContent, Button, Input, Loading, ErrorMessage } from '../components/common';
import { useCustomersPG } from '../hooks/useCustomersPG';
import { CustomerModal } from '../components/modals/CustomerModal';
import { UserPlus, Upload, ChevronLeft, ChevronRight } from 'lucide-react';
import CustomerAPI from '../services/api/customerAPI';

import {
  useTranslations
} from '../hooks/useTranslations';

export function Customers({ onNavigate }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showImportResults, setShowImportResults] = useState(false);
  const fileInputRef = useRef(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const t = useTranslations();

  const {
    customers,
    loading,
    error,
    handleCustomerSubmit,
    searchCustomers,
    clearError,
    loadAllCustomers,
  } = useCustomersPG();

  useEffect(() => {
    const performSearch = async () => {
      if (!searchTerm.trim()) {
        setFilteredCustomers(customers);
        setCurrentPage(1);
        return;
      }

      if (searchTerm.trim().length < 3) {
        setFilteredCustomers([]);
        return;
      }

      setSearchLoading(true);
      try {
        const results = await searchCustomers(searchTerm, true);
        setFilteredCustomers(results);
        setCurrentPage(1);
      } catch (error) {
        console.error('Search error:', error);
        setFilteredCustomers(customers);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounceTimer = setTimeout(performSearch, 500);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, searchCustomers, customers]);

  // Pagination calculations
  const totalCustomers = filteredCustomers.length;
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(totalCustomers / itemsPerPage);
  const startIndex = itemsPerPage === 'all' ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex = itemsPerPage === 'all' ? totalCustomers : startIndex + itemsPerPage;
  const paginatedCustomers = filteredCustomers.slice(startIndex, endIndex);

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === 'all' ? 'all' : parseInt(value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAddCustomer = () => {
    setModalMode('add');
    setSelectedCustomer(null);
    setIsModalOpen(true);
  };

  const handleViewDetails = (customer) => {
    onNavigate('customer-detail', customer.id);
  };

  const handleEditCustomer = (customer, event) => {
    if (event) {
      event.stopPropagation();
    }
    setModalMode('edit');
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    // Push the last value
    values.push(current.trim());

    return values;
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    // Parse header using proper CSV parsing
    const headers = parseCSVLine(lines[0]);

    // Parse rows
    const customers = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length !== headers.length) {
        console.warn(`Row ${i + 1} has ${values.length} columns but expected ${headers.length}, skipping`);
        continue;
      }

      const customer = {};
      headers.forEach((header, index) => {
        customer[header] = values[index] || null;
      });

      // Map 'name' to 'full_name' if needed
      if (customer.name && !customer.full_name) {
        customer.full_name = customer.name;
        delete customer.name;
      }

      customers.push(customer);
    }

    return customers;
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input
    event.target.value = '';

    if (!file.name.endsWith('.csv')) {
      alert('אנא בחר קובץ CSV בלבד');
      return;
    }

    setIsImporting(true);
    setImportResults(null);

    try {
      const text = await file.text();
      const parsedCustomers = parseCSV(text);

      if (parsedCustomers.length === 0) {
        alert('לא נמצאו לקוחות בקובץ');
        return;
      }

      if (parsedCustomers.length > 500) {
        alert('ניתן לייבא עד 500 לקוחות בכל פעם');
        return;
      }

      console.log('Parsed customers from CSV:', parsedCustomers);

      // Call the import API
      const results = await CustomerAPI.importCustomers(parsedCustomers);

      console.log('Import results:', results);
      setImportResults(results);
      setShowImportResults(true);

      // Refresh customers list
      await loadAllCustomers(true);

    } catch (error) {
      console.error('Error importing customers:', error);
      alert(`שגיאה בייבוא לקוחות: ${error.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const handleCloseImportResults = () => {
    setShowImportResults(false);
    setImportResults(null);
  };

  return (
    <ContentLayout
      currentPage="customers"
      onNavigate={onNavigate}
      title={t.customerManagement}
      description={t.manageYourClientDatabase}
      primaryAction={{
        label: t.addCustomer,
        onClick: handleAddCustomer,
        icon: <UserPlus className="w-4 h-4" />
      }}
      secondaryActions={[
        {
          label: 'ייבא לקוחות',
          onClick: handleImportClick,
          icon: <Upload className="w-4 h-4" />,
          variant: 'outline'
        }
      ]}
    >
      <PageContainer px="md:px-6 lg:px-8 px-0">

        {loading && <Loading text={t.loadingCustomers} />}

        {error && (
          <ErrorMessage
            message={error}
            dismissible
            onDismiss={clearError}
            className="mb-6"
          />
        )}

        {/* Enhanced Search Section */}
        <Card className="mb-4 md:mb-8 shadow-sm border-0 md:rounded-lg rounded-none">
          <CardContent className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 md:px-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Input
                  placeholder={t.searchCustomersByNamePhoneEmail}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  leftIcon="🔍"
                  className="border-gray-200 bg-white shadow-sm focus:ring-2 focus:ring-green-100 focus:border-green-300 text-sm"
                />
                {searchLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                  </div>
                )}
              </div>
            </div>

            {searchTerm.trim() && (
              <div className="mt-3 text-xs md:text-sm text-gray-600 font-medium">
                {searchLoading ? (
                  <span className="flex items-center space-x-2">
                    <div className="animate-pulse w-2 h-2 bg-green-400 rounded-full"></div>
                    <span>מחפש...</span>
                  </span>
                ) : (
                  <span className="text-green-700">
                    נמצאו {filteredCustomers.length} לקוח{filteredCustomers.length === 1 ? '' : 'ות'}
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer List Table */}
        {!loading && !searchLoading && paginatedCustomers.length > 0 && (
          <Card className="shadow-sm border-0 overflow-hidden md:rounded-lg rounded-none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      שם
                    </th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      טלפון
                    </th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell">
                      אימייל
                    </th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider hidden xl:table-cell">
                      הערות
                    </th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      פעולות
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedCustomers.map((customer) => (
                    <tr
                      key={customer.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleViewDetails(customer)}
                    >
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <div className="flex items-center max-w-[170px] md:max-w-[250px]">
                          <div className="flex-shrink-0 h-8 w-8 md:h-10 md:w-10 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-50 border border-emerald-200/60 flex items-center justify-center">
                            <span className="text-base md:text-lg">👤</span>
                          </div>
                          <div className="mr-2 md:mr-4 min-w-0">
                            <div className="text-xs md:text-sm font-semibold text-gray-900 truncate" title={customer.full_name}>
                              {customer.full_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <div className="text-xs md:text-sm text-gray-900">{customer.phone || '—'}</div>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap hidden lg:table-cell">
                        <div className="text-sm text-gray-600 truncate max-w-xs">
                          {customer.email || '—'}
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 hidden xl:table-cell">
                        <div className="text-sm text-gray-600 truncate max-w-md">
                          {customer.notes || '—'}
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => handleEditCustomer(customer, e)}
                            className="p-1.5 md:p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 shadow-sm transition-colors"
                            title="עריכה"
                            aria-label="עריכה"
                          >
                            ✏️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Pagination Controls */}
        {!loading && !searchLoading && filteredCustomers.length > 0 && (
          <Card className="mb-4 shadow-sm border-0 md:rounded-lg rounded-none">
            <CardContent className="bg-white px-4 md:px-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-gray-600">הצג:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(e.target.value)}
                    className="px-2 md:px-3 py-1.5 md:py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-xs md:text-sm"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="all">הכל</option>
                  </select>
                  <span className="text-xs md:text-sm text-gray-600 hidden sm:inline">לקוחות בעמוד</span>
                </div>

                <div className="text-xs md:text-sm text-gray-600">
                  מציג {startIndex + 1}-{Math.min(endIndex, totalCustomers)} מתוך {totalCustomers}
                </div>

                {itemsPerPage !== 'all' && totalPages > 1 && (
                  <div className="flex items-center gap-1 md:gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-1.5 md:p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="עמוד קודם"
                    >
                      <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-2 md:px-3 py-1 rounded-md text-xs md:text-sm transition-colors ${currentPage === pageNum
                                ? 'bg-green-600 text-white'
                                : 'border border-gray-300 hover:bg-gray-50'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-1.5 md:p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="עמוד הבא"
                    >
                      <ChevronLeft className="w-3 h-3 md:w-4 md:h-4" />
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!loading && !searchLoading && paginatedCustomers.length === 0 && (
          <Card className="shadow-sm border-0">
            <CardContent>
              <div className="text-center py-16">
                <div className="text-8xl mb-6 opacity-50">👥</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {searchTerm ? 'לא נמצאו לקוחות' : 'אין לקוחות עדיין'}
                </h3>
                <p className="text-gray-600 mb-6 text-lg">
                  {searchTerm
                    ? 'נסה לשנות את מונחי החיפוש'
                    : 'התחל על ידי הוספת הלקוח הראשון שלך'
                  }
                </p>
                {!searchTerm && (
                  <Button
                    onClick={handleAddCustomer}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    הוסף את הלקוח הראשון שלך
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Hidden file input for CSV import */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {/* Import Loading Indicator */}
        {isImporting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="max-w-md">
              <CardContent className="p-8 text-center">
                <Loading text="מייבא לקוחות..." />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Import Results Modal */}
        {showImportResults && importResults && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-2xl w-full max-h-[80vh] overflow-auto">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-4">תוצאות ייבוא</h2>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-blue-600">{importResults.total}</div>
                    <div className="text-sm text-gray-600">סך הכל</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-green-600">{importResults.successful}</div>
                    <div className="text-sm text-gray-600">הצליחו</div>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-yellow-600">{importResults.skipped}</div>
                    <div className="text-sm text-gray-600">דולגו</div>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-red-600">{importResults.failed}</div>
                    <div className="text-sm text-gray-600">נכשלו</div>
                  </div>
                </div>

                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2 text-red-600">שגיאות ({importResults.errors.length})</h3>
                    <div className="max-h-60 overflow-y-auto bg-red-50 rounded-lg p-4">
                      {importResults.errors.map((error, index) => (
                        <div key={index} className="mb-2 pb-2 border-b border-red-200 last:border-0">
                          <div className="font-medium">שורה {error.row}</div>
                          <div className="text-sm text-red-700">{error.error}</div>
                          {error.data && (
                            <div className="text-xs text-gray-600 mt-1">
                              {error.data.full_name} - {error.data.phone}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {importResults.importedCustomers && importResults.importedCustomers.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2 text-green-600">
                      לקוחות שיובאו בהצלחה ({importResults.importedCustomers.length})
                    </h3>
                    <div className="max-h-40 overflow-y-auto bg-green-50 rounded-lg p-4">
                      {importResults.importedCustomers.slice(0, 10).map((customer, index) => (
                        <div key={index} className="text-sm mb-1">
                          {customer.full_name} - {customer.phone}
                        </div>
                      ))}
                      {importResults.importedCustomers.length > 10 && (
                        <div className="text-sm text-gray-600 mt-2">
                          ועוד {importResults.importedCustomers.length - 10} לקוחות...
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    onClick={handleCloseImportResults}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    סגור
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Customer Modal */}
        <CustomerModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleCustomerSubmit}
          customer={selectedCustomer}
          mode={modalMode}
        />
      </PageContainer>
    </ContentLayout>
  );
}